import { cva, type VariantProps } from 'class-variance-authority';
import {
  disabledState,
  focusRing,
  transitionBase,
  uiMarker,
  uiMarkerLayout,
} from './shared';

/**
 * STEP INDICATOR
 *
 * The list of steps beside a multi-step form: which one you are on, which are
 * done, which you can jump back to. The onboarding wizard drew this twice —
 * a horizontal strip under the header on a phone and a sidebar column on a
 * desktop — with the same three states spelled the same way in both files
 * and nothing shared between them. It is one structure with two orientations.
 *
 * The frame is part of the recipe: the strip sits under the header with a
 * rule beneath it, the column sits beside the form with a rule to its right.
 */
export const stepIndicatorRecipe = cva(
  [uiMarker, uiMarkerLayout, 'border-border'],
  {
    variants: {
      orientation: {
        horizontal: 'flex flex-col border-b bg-background px-4 py-3',
        vertical: 'flex w-80 flex-col gap-8 border-r p-6',
      },
    },
    defaultVariants: { orientation: 'horizontal' },
  }
);

/**
 * One step. A button, so the keyboard can reach it; `disabled` when the steps
 * before it are not complete, which is the app's rule for jumping ahead.
 */
export const stepIndicatorStepRecipe = cva(
  [uiMarker, 'flex text-left', transitionBase, focusRing, disabledState],
  {
    variants: {
      orientation: {
        /** A column: the tile above the label. */
        horizontal: 'flex-col items-center gap-1',
        /** A row, padded so the current one can carry a background. */
        vertical: 'w-full items-center gap-3 rounded-lg p-3 hover:bg-muted/50',
      },
      state: {
        current: '',
        complete: '',
        pending: '',
      },
    },
    compoundVariants: [
      // The horizontal step colours its tile, not its row.
      {
        orientation: 'vertical',
        state: 'current',
        class: 'bg-info-subtle text-info-subtle-foreground',
      },
      { orientation: 'vertical', state: 'complete', class: 'text-success' },
      {
        orientation: 'vertical',
        state: 'pending',
        class: 'text-muted-foreground',
      },
    ],
    defaultVariants: { orientation: 'horizontal', state: 'pending' },
  }
);

/** The tinted circle around the icon on the horizontal strip. */
export const stepIndicatorTileRecipe = cva(
  [uiMarker, 'inline-flex items-center justify-center rounded-full p-2'],
  {
    variants: {
      state: {
        current: 'bg-info-subtle text-info-subtle-foreground',
        complete: 'bg-success-subtle text-success',
        pending: 'bg-muted text-muted-foreground',
      },
    },
    defaultVariants: { state: 'pending' },
  }
);

export type StepIndicatorRecipeProps = VariantProps<typeof stepIndicatorRecipe>;
export type StepIndicatorStepRecipeProps = VariantProps<
  typeof stepIndicatorStepRecipe
>;
